import { Component, ElementRef, Input, OnInit, TemplateRef, ViewChild, output } from '@angular/core'
import { getContentEditableCaretCoords, isInputOrTextAreaElement } from './bv-mention-utils'
import { getCaretCoordinates } from './caret-coords'
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { NgTemplateOutlet } from '@angular/common';

/**
 * Angular 2 Mentions.
 * https://github.com/dmacfarlane/angular2-mentions
 *
 * Copyright (c) 2016 Dan MacFarlane
 */
@Component({
    selector: 'mention-list',
    template: `
    <ng-template #defaultItemTemplate let-item="item">
      <div class="media-image">
        <user-avatar [firstName]="item[labelKey]"></user-avatar>
      </div>
      <div class="media-name">{{ item[labelKey] }}</div>
    </ng-template>
    <ul #list [hidden]="hidden" class="bv-mention-list scrollable-menu">
      @for (item of items; track item; let i = $index) {
        <li
          [class.active]="activeIndex == i"
          class="bv-mention-list-item"
          (mousedown)="activeIndex = i; itemClick.emit(); $event.preventDefault()"
          >
          <ng-template [ngTemplateOutlet]="itemTemplate" [ngTemplateOutletContext]="{ item: item }"></ng-template>
        </li>
      }
    </ul>
    `,
    styles: [
        `
          .scrollable-menu {
            display: block;
            height: auto;
            max-height: 300px;
            overflow: auto;
          }
    
          [hidden] {
            display: none;
          }
    
          li.active {
            background-color: rgba(249, 250, 252, 0.8);
          }
    
          .bv-mention-list {
            padding: 0;
            margin: 0;
            list-style: none;
            background-color: white;
            box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.2);
          }
          .bv-mention-list-item {
            color: #475056;
            font-size: 15px;
            padding: 6px;
            display: flex;
            flex-grow: 1;
            flex-direction: flex-start;
          }
          .media-image {
            margin-right: 10px;
          }
        `,
    ],
    imports: [UserAvatarComponent, NgTemplateOutlet]
})
export class MentionListComponent implements OnInit {
  @Input() labelKey = 'label'
  @Input() itemTemplate: TemplateRef<any>
  readonly itemClick = output();
  @ViewChild('list', { static: true }) list: ElementRef
  @ViewChild('defaultItemTemplate', { static: true }) defaultItemTemplate: TemplateRef<any>
  items = []
  activeIndex = 0
  hidden = false
  constructor(private _element: ElementRef) {}

  ngOnInit() {
    if (!this.itemTemplate) {
      this.itemTemplate = this.defaultItemTemplate
    }
  }

  // lots of confusion here between relative coordinates and containers
  position(nativeParentElement: HTMLInputElement, iframe: HTMLIFrameElement = null) {
    let coords = { top: 0, left: 0 }
    if (isInputOrTextAreaElement(nativeParentElement)) {
      // parent elements need to have postition:relative for this to work correctly?
      coords = getCaretCoordinates(nativeParentElement, nativeParentElement.selectionStart)
      coords.top = nativeParentElement.offsetTop
      coords.left = nativeParentElement.offsetLeft
    } else if (iframe) {
      const context: { iframe: HTMLIFrameElement; parent: Element } = { iframe: iframe, parent: iframe.offsetParent }
      coords = getContentEditableCaretCoords(context)
    } else {
      const doc = document.documentElement
      const scrollLeft = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0)
      const scrollTop = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0)

      // bounding rectangles are relative to view, offsets are relative to container?
      const caretRelativeToView = getContentEditableCaretCoords({ iframe: iframe })
      const parentRelativeToContainer: ClientRect = nativeParentElement.getBoundingClientRect()

      coords.top = caretRelativeToView.top - parentRelativeToContainer.top + nativeParentElement.offsetTop - scrollTop
      coords.left =
        caretRelativeToView.left - parentRelativeToContainer.left + nativeParentElement.offsetLeft - scrollLeft
    }
    const el: HTMLElement = this._element.nativeElement
    el.style.position = 'absolute'
    el.style.left = coords.left + 'px'
    el.style.top = coords.top + 'px'
    el.style.width = nativeParentElement.offsetWidth + 'px'
    el.style.transform = 'translateY(-118%)'
  }

  get activeItem() {
    return this.items[this.activeIndex]
  }

  activateNextItem() {
    // adjust scrollable-menu offset if the next item is out of view
    const listEl: HTMLElement = this.list.nativeElement
    const activeEl = listEl.getElementsByClassName('active').item(0)
    if (activeEl) {
      const nextLiEl: HTMLElement = <HTMLElement>activeEl.nextSibling
      if (nextLiEl && nextLiEl.nodeName == 'LI') {
        const nextLiRect: ClientRect = nextLiEl.getBoundingClientRect()
        if (nextLiRect.bottom > listEl.getBoundingClientRect().bottom) {
          listEl.scrollTop = nextLiEl.offsetTop + nextLiRect.height - listEl.clientHeight
        }
      }
    }
    // select the next item
    this.activeIndex = Math.max(Math.min(this.activeIndex + 1, this.items.length - 1), 0)
  }

  activatePreviousItem() {
    // adjust the scrollable-menu offset if the previous item is out of view
    const listEl: HTMLElement = this.list.nativeElement
    const activeEl = listEl.getElementsByClassName('active').item(0)
    if (activeEl) {
      const prevLiEl: HTMLElement = <HTMLElement>activeEl.previousSibling
      if (prevLiEl && prevLiEl.nodeName == 'LI') {
        const prevLiRect: ClientRect = prevLiEl.getBoundingClientRect()
        if (prevLiRect.top < listEl.getBoundingClientRect().top) {
          listEl.scrollTop = prevLiEl.offsetTop
        }
      }
    }
    // select the previous item
    this.activeIndex = Math.max(Math.min(this.activeIndex - 1, this.items.length - 1), 0)
  }

  resetScroll() {
    this.list.nativeElement.scrollTop = 0
  }
}
